import { createSlice } from "@reduxjs/toolkit";
import {
  createTask,
  fetchTasks,
  updateTask,
  deleteTask,
  createSubtask,
  updateSubtask,
  deleteSubtask,
  moveTaskBetweenLists,
  createTaskFromSubtask,
} from "../service/TaskService";

const tasksInitialState = {
  taskData: {
    todo: [],
    inProgress: [],
    completed: [],
  },
  totalTodoTasks: 0,
  totalInProgressTasks: 0,
  totalCompletedTasks: 0,
  isLoading: false,
  error: null,
  filteredData: {
    filteredDateCriteria: "createdAt",
    filterdDateRange: "",
    filteredProject: 0,
    filteredClient: 0,
    filteredTaskStatus: "all",
    filteredPriority: 0,
  },
  toggleFilter: true,
  sortingData: {},
  page: 1,
  openSubtaskId: null,
  activeTask: null,
};

export const taskSlice = createSlice({
  name: "tasks",
  initialState: tasksInitialState,
  reducers: {
    filterAction: (state, action) => {
      state.filteredData = { ...state.filteredData, ...action.payload };
      state.page = 1;
      state.openSubtaskId = null;
    },
    toggleAction: (state) => {
      state.toggleFilter = !state.toggleFilter;
      state.page = 1;
    },
    sortingAction: (state, action) => {
      state.sortingData = { ...state.sortingData, ...action.payload };
      state.page = 1;
      state.openSubtaskId = null;
    },

    setTasksOrder: (state, action) => {
      const { status, tasks } = action.payload;
      state.taskData[status] = tasks;
    },

    setPage(state, action) {
      state.page = action.payload;
    },

    toggleSubtasks(state, action) {
      state.openSubtaskId = action.payload;
    },

    setActiveTask(state, action) {
      state.activeTask = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // create task
      .addCase(createTask.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        const tasks = state.taskData[action.payload.status];
        const newTasks = [action.payload, ...tasks].slice(0, 7);

        const status =
          action.payload.status[0].toUpperCase() +
          action.payload.status.slice(1);

        state.taskData[action.payload.status] = newTasks;
        state[`total${status}Tasks`] += 1;
        state.isLoading = false;
      })
      .addCase(createTask.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      // fetch tasks
      .addCase(fetchTasks.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchTasks.fulfilled,
        (
          state,
          {
            payload: {
              groupByStatus,
              allTodoTasks,
              allInProgressTasks,
              allCompletedTasks,
            },
          }
        ) => {
          state.isLoading = false;
          state.taskData = groupByStatus;
          state.totalTodoTasks = allTodoTasks;
          state.totalInProgressTasks = allInProgressTasks;
          state.totalCompletedTasks = allCompletedTasks;
        }
      )
      .addCase(fetchTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      // delete task
      .addCase(deleteTask.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteTask.fulfilled, (state, { payload: { id, status } }) => {
        // const tasks = state.taskData[status];
        // const filtered = tasks.filter((task) => task._id !== id);

        // const _status = status[0].toUpperCase() + status.slice(1);

        // state.taskData[status] = filtered;
        // state[`total${_status}Tasks`] -= 1;
        state.isLoading = false;
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      .addCase(updateTask.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        const { id, data, status } = action.payload;

        const taskIndex = state.taskData[status].findIndex(
          (task) => task._id === id
        );

        const task = state.taskData[status][taskIndex];

        if (taskIndex !== -1) {
          state.taskData[status][taskIndex] = {
            ...data,
            subTasks: task.subTasks || [],
          };
        }
        state.isLoading = false;
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      .addCase(createSubtask.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createSubtask.fulfilled, (state, action) => {
        const { index, data } = action.payload;
        const task = state.taskData[data.status][index];

        if (!task.subTasks) {
          task.subTasks = [data];
        } else {
          task.subTasks = [data, ...task.subTasks];
        }

        state.isLoading = false;
      })
      .addCase(createSubtask.rejected, (state, action) => {
        state.error = action.error.message;
        state.isLoading = false;
      })

      .addCase(updateSubtask.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateSubtask.fulfilled, (state, action) => {
        const { data, index } = action.payload;
        const subtasks = state.taskData[data.status][index].subTasks;

        const subtaskIndex = subtasks.findIndex(
          (subtask) => subtask._id === data._id
        );

        subtasks[subtaskIndex] = data;
        state.isLoading = false;
      })
      .addCase(updateSubtask.rejected, (state, action) => {
        state.error = action.error.message;
        state.isLoading = false;
      })

      .addCase(deleteSubtask.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteSubtask.fulfilled, (state, action) => {
        const { status, subtaskId, index } = action.payload;

        const tasks = state.taskData[status];
        const task = tasks[index];

        state.taskData[status][index].subTasks = task.subTasks.filter(
          (subtask) => subtask._id !== subtaskId
        );
        state.isLoading = false;
      })
      .addCase(deleteSubtask.rejected, (state, action) => {
        state.error = action.error.message;
        state.isLoading = false;
      })

      .addCase(moveTaskBetweenLists.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(moveTaskBetweenLists.fulfilled, (state, action) => {
        const { from, to, sourceTasks, destinationTasks } = action.payload;

        const statusFrom = from[0].toUpperCase() + from.slice(1);
        const statusTo = to[0].toUpperCase() + to.slice(1);

        state.taskData[from] = sourceTasks;
        state.taskData[to] = destinationTasks;

        state[`total${statusFrom}Tasks`] -= 1;
        state[`total${statusTo}Tasks`] += 1;

        state.isLoading = false;
      })
      .addCase(moveTaskBetweenLists.rejected, (state, action) => {
        state.error = action.error.message;
        state.isLoading = false;
      })

      .addCase(createTaskFromSubtask.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createTaskFromSubtask.fulfilled, (state, action) => {
        const { oldStatus, index, data } = action.payload;
        const tasks = state.taskData[oldStatus];

        const deleteSubtaskFrom = tasks[index].subTasks.filter(
          (subtask) => subtask._id !== data._id
        );
        tasks[index].subTasks = deleteSubtaskFrom;

        state.taskData[data.status] = [
          data,
          ...state.taskData[data.status],
        ].slice(0, 7);

        state[
          `total${data.status[0].toUpperCase() + data.status.slice(1)}Tasks`
        ] += 1;

        state.isLoading = false;
      })
      .addCase(createTaskFromSubtask.rejected, (state, action) => {
        state.error = action.error.message;
        state.isLoading = false;
      });
  },
});

export const taskActions = taskSlice.actions;

export default taskSlice.reducer;
